"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import { motion } from "framer-motion";
import {
  Activity,
  BarChart3,
  CheckCircle2,
  Cpu,
  Database,
  Gauge,
  Layers,
  LineChart,
  ShieldCheck,
  TrendingUp,
  Trophy,
  Upload,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import * as React from "react";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import marketImage from "@/public/images/market.jpeg";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const trustStrip = [
  "30+ models",
  "12 evaluation metrics",
  "Cross-validation",
  "Serverless GPU workers",
  "CSV · Parquet · JSON",
  "API keys + SSO sessions",
];

const models = [
  "AutoARIMA",
  "ETS",
  "MSTL",
  "LightGBM",
  "XGBoost",
  "NHITS",
  "TFT",
  "Chronos",
  "TimesFM",
  "Moirai",
  "TimeGPT",
  "Hierarchical reconcilers",
];

export default function TimePage() {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {/* Hero Section with Background */}
        <section className="relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <Image
              src={marketImage}
              alt="Time-series forecasting visualization"
              className="object-cover object-center w-full h-full opacity-40 dark:opacity-30"
              fill
              priority
              sizes="100vw"
              quality={90}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-orange-900/40 to-background/95" />
          </div>

          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_80%)] pointer-events-none z-10 opacity-30" />

          <motion.div
            className="absolute top-20 left-8 w-16 h-16 bg-amber-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-amber-500/20 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <LineChart className="w-8 h-8 text-amber-500/70" />
          </motion.div>
          <motion.div
            className="absolute top-32 right-12 w-14 h-14 bg-orange-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-orange-500/20 z-10"
            style={{ animationDelay: "1s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Trophy className="w-7 h-7 text-orange-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-32 left-16 w-14 h-14 bg-yellow-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-yellow-500/20 z-10"
            style={{ animationDelay: "2s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <BarChart3 className="w-7 h-7 text-yellow-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 w-14 h-14 bg-amber-600/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-amber-600/20 z-10"
            style={{ animationDelay: "0.5s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Gauge className="w-7 h-7 text-amber-600/70" />
          </motion.div>

          <div className="relative z-20 section-container">
            <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
                  style={{
                    backgroundColor: isLight
                      ? "rgba(17, 24, 39, 0.2)"
                      : "rgba(255, 255, 255, 0.2)",
                    color: isLight ? "#111827" : "#ffffff",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isLight
                      ? "rgba(17, 24, 39, 0.3)"
                      : "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <LineChart
                    className="w-4 h-4 mr-2"
                    style={{ color: isLight ? "#111827" : "#ffffff" }}
                  />
                  Kautious Time
                </span>
                <h1
                  className="mb-6"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  Own the number before the meeting starts.
                </h1>
                <p
                  className="text-xl mb-8 leading-relaxed max-w-4xl"
                  style={{
                    color: isLight ? "#374151" : "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Kautious Time turns time-series data into forecasts you can
                  defend. Upload your data, run 30+ statistical, machine
                  learning, neural, foundation, and hierarchical models, then
                  compare the winners with backtested metrics and confidence
                  intervals. No spreadsheet ritual. No one-model guess.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                  <Button
                    size="lg"
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: isLight ? "#d97706" : "#f59e0b",
                      color: "#ffffff",
                    }}
                    asChild
                  >
                    <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                      Request early access
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="hover:opacity-80"
                    style={{
                      borderColor: isLight ? "#111827" : "#ffffff",
                      color: isLight ? "#111827" : "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    asChild
                  >
                    <Link href="#tournament">Compare models</Link>
                  </Button>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {trustStrip.map((item) => (
                    <span
                      key={item}
                      className="text-xs rounded-full px-3 py-1.5 backdrop-blur-sm"
                      style={{
                        color: isLight ? "#374151" : "rgba(255,255,255,0.85)",
                        border: `1px solid ${isLight ? "rgba(17,24,39,0.2)" : "rgba(255,255,255,0.25)"}`,
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Enemy */}
        <section className="section-padding bg-secondary/5">
          <div className="section-container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              The old forecasting workflow makes smart teams look uncertain.
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Somewhere in the planning cycle, a forecast becomes a negotiation.
              One team has a spreadsheet. Another has a notebook. Someone trusts
              last year's model because it is already in production. Someone
              else has a feeling about next quarter.
            </p>
            <p className="text-lg text-foreground">
              That is not forecasting. It is a ritual. Kautious Time replaces
              the ritual with a repeatable model tournament — every forecast
              tested, compared, ranked, and explained before it reaches the room
              where decisions get made.
            </p>
          </div>
        </section>

        {/* The First Win */}
        <section className="section-padding">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Your first credible forecast in minutes.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Upload,
                  endpoint: "POST /data/upload",
                  title: "Upload the data",
                  body: "Bring CSV, Parquet, or JSON. Kautious Time validates the shape, maps your time and target columns, detects frequency, and stores the dataset.",
                },
                {
                  icon: Cpu,
                  endpoint: "POST /forecast",
                  title: "Launch the forecast",
                  body: "Pick specific models or use auto mode to evaluate multiple candidates. Run single-model jobs, multi-model jobs, or automatic selection.",
                },
                {
                  icon: BarChart3,
                  endpoint: "GET /forecast/jobs/{id}",
                  title: "Show the evidence",
                  body: "Poll progress, inspect results, download JSON/CSV/Parquet, and use metrics to explain why the selected model earned the forecast.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="p-8 card-hover">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <code className="text-xs font-mono text-amber-600 mb-2 block">
                      {item.endpoint}
                    </code>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* The Model Tournament */}
        <section id="tournament" className="section-padding bg-muted/30">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Let the models fight. Ship the one that earns it.
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Don't bet your planning cycle on a single model. Use
                cross-validation and evaluation metrics to decide what works on
                your data — not what looked best in a vendor benchmark. When
                anyone asks "why this forecast?", you have the leaderboard.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {models.map((model) => (
                <span
                  key={model}
                  className="text-sm text-foreground/80 border border-border bg-background rounded-full px-3 py-1.5"
                >
                  {model}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "Statistical models for strong classical baselines.",
                "ML models with lag features for tabular forecasting patterns.",
                "Neural and foundation models for richer sequence behavior.",
                "Hierarchical reconciliation when bottom-level and aggregate forecasts need to agree.",
                "Comparisons scored with MAE, RMSE, MAPE, SMAPE, WMAPE, MASE, bias, CRPS, coverage, Winkler score, and pinball loss.",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                  <p className="text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Buyer */}
        <section className="section-padding">
          <div className="section-container">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Built for the person who has to own the forecast.
              </h2>
              <p className="text-lg text-muted-foreground">
                You are not just buying a forecasting API. You are buying a more
                defensible planning conversation.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[
                {
                  title: "A better answer than “this is the model we use.”",
                  body: "Show which models were tested and why one won.",
                },
                {
                  title: "A cleaner review with leadership.",
                  body: "Bring charts, ranked metrics, and confidence intervals instead of a fragile spreadsheet.",
                },
                {
                  title: "A faster path from data to decision.",
                  body: "Launch async jobs without waiting on local environments or GPU setup.",
                },
                {
                  title: "A system your team can repeat.",
                  body: "Upload, forecast, compare, download, and audit through one API and admin dashboard.",
                },
              ].map((item) => (
                <Card key={item.title} className="p-8 card-hover">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        {item.body}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Product Proof */}
        <section className="section-padding bg-muted/30">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Serious forecasting without the infrastructure drag.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Layers,
                  title: "Model coverage",
                  body: "30+ registered models across statistical, ML, neural, foundation, and hierarchical families.",
                },
                {
                  icon: Activity,
                  title: "Backtesting and metrics",
                  body: "Cross-validation and 12 evaluation metrics help separate signal from model preference.",
                },
                {
                  icon: Cpu,
                  title: "GPU-backed execution",
                  body: "Neural and local foundation models run through serverless worker infrastructure instead of your laptop.",
                },
                {
                  icon: TrendingUp,
                  title: "Async jobs",
                  body: "Long-running forecasts run as background jobs with status, progress, cancellation, and result download.",
                },
                {
                  icon: Database,
                  title: "Data validation",
                  body: "Column mapping, timestamp parsing, numeric target checks, duplicate detection, and automatic frequency detection.",
                },
                {
                  icon: ShieldCheck,
                  title: "Secure access",
                  body: "API keys, session auth, JWT, and tenant-scoped datasets and jobs when auth is configured.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="p-8 card-hover">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Early Access CTA */}
        <section className="section-padding bg-gradient-to-br from-amber-600 to-orange-600 text-white">
          <div className="section-container text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-4 text-white/90">
                <Trophy className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-[0.2em]">
                  Early access is gated because setup matters
                </span>
              </div>
              <h2 className="text-4xl font-semibold mb-6 text-white">
                Backtested or it didn't happen.
              </h2>
              <p className="text-xl mb-8 text-white/90">
                For teams that already feel the cost of uncertain forecasts:
                demand planning, finance, capacity, inventory, and revenue
                planning. We onboard fewer teams, well.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-amber-600 hover:bg-white/90"
                  asChild
                >
                  <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                    Request your invite
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/daytta">See it inside Daytta</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
